import ee from '../../services/earth-engine';
import { convertFeaturesToCSV, evaluateEEFeatures } from './download';
import { observedDepthImage } from './depthImage';
import { addSPM02, SPM_BAND } from './spm';
import { spectralBandsOf } from './trees';

/**
 * @function pixelCoordinateBands
 * @description An `x`/`y` band pair in the CRS the survey was given in, so each exported row
 * carries the coordinate a GIS will read it back at. A projected CRS is snapped to the pixel grid
 * at the sampling scale; a geographic one is simply longitude and latitude.
 * @param {String} crs - The CRS, as an EPSG string.
 * @param {Number} scale - Pixel size in metres.
 * @memberof module:Bathymetry
 * @returns {Object} An `ee.Image` carrying `x` and `y`.
 */
const pixelCoordinateBands = (crs, scale) => {
  if (String(crs || '').indexOf('4326') !== -1) return ee.Image.pixelLonLat().rename(['x', 'y']);
  const projection = ee.Projection(crs);
  return ee.Image.pixelCoordinates(projection).rename(['x', 'y']).reproject(projection.atScale(scale));
};

/**
 * @function collectPixelRows
 * @description Reads every pixel of the area as one CSV row: the coordinate, the reflectance of
 * each optical band the mission carries, the suspended matter estimate, the modelled depth before
 * and after the tide correction, and the surveyed depth where a sounding falls on that pixel.
 * The reflectances are there because this file is also what the tree models are trained from,
 * which need the predictors next to the label rather than the depth alone - so every optical band
 * is written, not only the three the empirical models use.
 * Pixels without a sounding are kept: the sampling drops no nulls, and a constant `keep` band
 * holds in the rows whose every spectral value is masked.
 * An area too large to sample in one request arrives already split into parts, which are sampled
 * in turn and concatenated - see {@link planRegionParts}.
 * @param {Object} params
 * @param {Object} params.depthImage - The run's result, as an `ee.Image`.
 * @param {Object} params.sourceImage - The imagery the run was made from.
 * @param {Array<String>} params.availableBands - The band names the result carries.
 * @param {Object} params.data - The analysis configuration.
 * @param {Object} params.fileData - The uploaded files' state.
 * @param {Object} [params.missionBands] - The mission's band map.
 * @param {Number} params.scale - Pixel size in metres.
 * @param {Object} params.region - The whole area, as an `ee.Geometry`.
 * @param {Object} params.plan - The sampling plan, as {@link planRegionParts} returns it.
 * @param {Boolean} params.hasTide - Whether the tide correction ran.
 * @param {Function} [params.onPart] - Called with `(current, total)` before each part.
 * **Called by:
 * @see {ProcessingPage} - Bathymetry's result page, on the pixel CSV export.
 * @memberof module:Bathymetry
 * @returns {Promise<Object>} `{csv, selectors, features}`.
 */
export const collectPixelRows = async ({
  depthImage,
  sourceImage,
  availableBands,
  data,
  fileData,
  missionBands,
  scale,
  region,
  plan,
  hasTide,
  onPart,
}) => {
  const spectralBands = missionBands ? spectralBandsOf(missionBands) : (data?.bands || []).filter(Boolean);
  const selectors = [
    'x',
    'y',
    ...spectralBands,
    ...(missionBands ? [SPM_BAND] : []),
    'pred_depth',
    'pred_depth_raw',
    ...(hasTide && availableBands.includes('tideLevel') ? ['tideLevel'] : []),
    'or_depth',
  ];

  let stack = depthImage
    .addBands(pixelCoordinateBands(fileData.coordinatesSystem, scale), null, true)
    .addBands(observedDepthImage(fileData.matrix, ee.Reducer.first(), region), null, true);
  if (spectralBands.length) {
    const reflectance = missionBands
      ? addSPM02(ee.Image(sourceImage), missionBands).select([...spectralBands, SPM_BAND])
      : ee.Image(sourceImage).select(spectralBands);
    stack = stack.addBands(reflectance.toFloat().clip(region), null, true);
  }
  const sampled = ee.Image.constant(1)
    .rename('keep')
    .updateMask(ee.Image(1))
    .addBands(stack.toFloat().select(selectors));

  const samplePart = (geometry) =>
    evaluateEEFeatures(
      ee
        .FeatureCollection(
          sampled.sample({
            region: geometry,
            scale,
            geometries: false,
            tileScale: 16,
            numPixels: 2e6,
            dropNulls: false,
          }),
        )
        .map((f) => ee.Feature(null, ee.Feature(f).toDictionary(selectors))),
    );

  let features = [];
  if (plan.parts === 1) {
    features = await samplePart(region);
  } else {
    for (let i = 0; i < plan.parts; i++) {
      onPart?.(i + 1, plan.parts);
      const part = await samplePart(ee.Geometry(plan.geometries.get(i)));
      console.log(`[collectPixelRows] área ${i + 1} de ${plan.parts}: ${part.length} pixels retornados.`);
      features = features.concat(part);
    }
  }

  const withSounding = features.filter((f) => f?.properties?.or_depth != null).length;
  console.log(`Linhas com or_depth: ${withSounding} de ${features.length}`);
  return { selectors, features, csv: convertFeaturesToCSV(features, selectors) };
};
